import type { EventCallback, IPubSubService } from './types';

/**
 * Abstract base class providing pub/sub event capabilities for visualization services.
 * All visualization services should extend this class to gain event broadcasting
 * and subscription functionality.
 *
 * @example
 * ```typescript
 * class MyService extends PubSubService {
 *     readonly EVENTS = {
 *         DATA_LOADED: "myservice:data:loaded",
 *         STATE_CHANGED: "myservice:state:changed",
 *     } as const;
 *
 *     loadData(data: MyData): void {
 *         // ... process data
 *         this.broadcast(this.EVENTS.DATA_LOADED, { data });
 *     }
 * }
 * ```
 */
export abstract class PubSubService implements IPubSubService {
    /**
     * Events this service can emit. Must be defined by subclasses.
     * Use `as const` for type safety on event names.
     */
    abstract readonly EVENTS: Record<string, string>;

    /** Map of event names to their subscriber callbacks */
    private listeners: Map<string, Set<EventCallback>> = new Map();

    /** Set of cleanup functions for debounced subscriptions */
    private debounceCleanups: Set<() => void> = new Set();

    /**
     * Subscribe to an event emitted by this service.
     *
     * @param eventName - The name of the event to subscribe to (must be in EVENTS)
     * @param callback - Function to call when the event is emitted
     * @returns Unsubscribe function - call this to remove the subscription
     * @throws Error if eventName is not a valid event for this service
     *
     * @example
     * ```typescript
     * const unsubscribe = service.subscribe(
     *     service.EVENTS.DATA_LOADED,
     *     (data) => console.log("Data loaded:", data)
     * );
     *
     * // Later, to unsubscribe:
     * unsubscribe();
     * ```
     */
    subscribe<T = unknown>(eventName: string, callback: EventCallback<T>): () => void {
        this.validateEvent(eventName);

        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }
        this.listeners.get(eventName)!.add(callback as EventCallback);

        // Return unsubscribe function
        return () => {
            this.listeners.get(eventName)?.delete(callback as EventCallback);
        };
    }

    /**
     * Subscribe to an event with debouncing for high-frequency events.
     * Uses a "leading + trailing" pattern:
     * - First event is processed immediately
     * - Subsequent events within the delay window are debounced
     * - After the delay, the last event data is processed (if different from first)
     *
     * This ensures that even with continuous rapid events, callbacks are invoked
     * at least every `delay` milliseconds.
     *
     * @param eventName - The name of the event to subscribe to (must be in EVENTS)
     * @param callback - Function to call when the event is emitted (debounced)
     * @param delay - Debounce delay in milliseconds (default: 300)
     * @returns Unsubscribe function - call this to remove the subscription
     * @throws Error if eventName is not a valid event for this service
     *
     * @example
     * ```typescript
     * const unsubscribe = service.subscribeDebounced(
     *     service.EVENTS.POSITION_CHANGED,
     *     (position) => updateExpensiveVisualization(position),
     *     500  // First call immediate, then at most every 500ms
     * );
     * ```
     */
    subscribeDebounced<T = unknown>(eventName: string, callback: EventCallback<T>, delay = 300): () => void {
        // Each subscription has its own debounce state
        let timerId: ReturnType<typeof setTimeout> | null = null;
        let lastData: unknown = undefined;
        let hasTrailingData = false;

        const cleanup = () => {
            if (timerId) {
                clearTimeout(timerId);
                timerId = null;
            }
            hasTrailingData = false;
        };

        this.debounceCleanups.add(cleanup);

        const debouncedCallback = (data: unknown) => {
            lastData = data;

            if (timerId === null) {
                // Leading edge: no active timer, call immediately
                callback(data as T);

                // Start cooldown period
                timerId = setTimeout(() => {
                    timerId = null;
                    // Trailing edge: if we have pending data, call with last value
                    if (hasTrailingData) {
                        hasTrailingData = false;
                        // Re-trigger with the last data, which will hit the leading edge
                        debouncedCallback(lastData);
                    }
                }, delay);
            } else {
                // During cooldown: just mark that we have trailing data
                hasTrailingData = true;
            }
        };

        const unsubscribeFromEvent = this.subscribe(eventName, debouncedCallback);

        return () => {
            cleanup();
            this.debounceCleanups.delete(cleanup);
            unsubscribeFromEvent();
        };
    }

    /**
     * Broadcast an event to all subscribers.
     * This method is protected - only the service itself should emit events.
     *
     * @param eventName - The name of the event to broadcast (must be in EVENTS)
     * @param data - Optional data to pass to subscribers
     * @throws Error if eventName is not a valid event for this service
     */
    protected broadcast(eventName: string, data?: unknown): void {
        this.validateEvent(eventName);

        const callbacks = this.listeners.get(eventName);
        if (callbacks) {
            callbacks.forEach((callback) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in event handler for ${eventName}:`, error);
                }
            });
        }
    }

    /**
     * Remove all subscriptions and clean up resources.
     * Called when the service needs to be reset (e.g., when closing a case).
     */
    reset(): void {
        // Clear all debounce timers
        this.debounceCleanups.forEach((cleanup) => cleanup());
        this.debounceCleanups.clear();

        // Clear all listeners
        this.listeners.clear();
    }

    /**
     * Validate that an event name is defined in this service's EVENTS.
     *
     * @param eventName - The event name to validate
     * @throws Error if the event name is not valid
     */
    private validateEvent(eventName: string): void {
        const validEvents = Object.values(this.EVENTS);
        if (!validEvents.includes(eventName)) {
            throw new Error(`Invalid event "${eventName}". Valid events: ${validEvents.join(', ')}`);
        }
    }
}
